import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Phone, Navigation, ChevronRight } from "lucide-react";
import { Reveal, RevealGroup, RevealItem } from "@/components/site/Reveal";
import Kontakt from "@/components/site/Kontakt";
import Footer from "@/components/site/Footer";
import SectionDivider from "@/components/site/SectionDivider";
import {
  SERVICE_PAGES,
  PHONE_DISPLAY,
  PHONE_RAW,
  MAPS_URL,
} from "@/data/content";

const NAVY = "#093a5a";
const WHITE = "#ffffff";
const ORANGE = "#f97316";
const EASE = [0.22, 1, 0.36, 1];

export default function ServicePage({ slug, onNav }) {
  const data = SERVICE_PAGES[slug];

  useEffect(() => {
    if (!data) return;
    const prevTitle = document.title;
    document.title = data.seoTitle;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute("content") : null;
    if (meta) meta.setAttribute("content", data.seoDesc);
    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc !== null) meta.setAttribute("content", prevDesc);
    };
  }, [slug, data]);

  if (!data) return null;
  const Icon = data.icon;

  return (
    <>
      {/* SERVICE HERO */}
      <section
        data-testid="service-hero"
        className="relative flex items-center justify-center overflow-hidden bg-brand-navy2 min-h-[300px] md:min-h-[420px] pt-16 md:pt-20"
      >
        <div className="absolute inset-0">
          <img
            src={data.heroImg}
            alt={data.h1}
            className="h-full w-full object-cover object-center"
          />
          <div
            className="absolute inset-0"
            style={{ backgroundColor: "rgba(9,42,66,0.80)" }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(9,58,90,0.30) 0%, rgba(7,42,66,0.55) 100%)",
            }}
          />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl w-full px-5 sm:px-8 py-14 md:py-20 text-center">
          <motion.nav
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="flex items-center justify-center gap-1.5 text-sm text-slate-300 mb-5"
            data-testid="service-breadcrumb"
          >
            <Link to="/" className="hover:text-brand-orange transition-colors">
              Domů
            </Link>
            <ChevronRight className="h-4 w-4 text-brand-orange" />
            <span className="text-white font-semibold">{data.h1}</span>
          </motion.nav>

          <motion.span
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: EASE, delay: 0.1 }}
            className="inline-grid place-items-center h-14 w-14 rounded-2xl bg-brand-orange text-white shadow-lg shadow-brand-orange/30 mb-5"
          >
            <Icon className="h-7 w-7" strokeWidth={2.2} />
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
            data-testid="service-title"
            className="font-heading font-black text-white tracking-tight text-4xl sm:text-5xl md:text-6xl"
          >
            {data.h1}
          </motion.h1>
        </div>
      </section>

      <SectionDivider to={WHITE} accent={ORANGE} />

      {/* CONTENT */}
      <section data-testid="service-content" className="relative bg-white py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* left: intro + list */}
            <div className="lg:col-span-7">
              <Reveal mount>
                <p className="text-sm font-bold uppercase tracking-[0.2em] text-brand-orange mb-3">
                  {data.h1}
                </p>
                <p className="text-lg sm:text-xl text-slate-700 leading-relaxed">
                  {data.intro}
                </p>
              </Reveal>

              <Reveal mount delay={0.1}>
                <h2 className="mt-10 font-heading font-black text-2xl sm:text-3xl text-brand-navy tracking-tight">
                  {data.listTitle}
                </h2>
              </Reveal>

              <RevealGroup mount className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-3.5" stagger={0.05}>
                {data.list.map((item) => (
                  <RevealItem key={item}>
                    <div
                      data-testid="service-list-item"
                      className="flex items-start gap-3"
                    >
                      <CheckCircle2
                        className="h-5 w-5 text-brand-orange shrink-0 mt-0.5"
                        strokeWidth={2.2}
                      />
                      <span className="text-[15px] text-slate-700 leading-snug">
                        {item}
                      </span>
                    </div>
                  </RevealItem>
                ))}
              </RevealGroup>
            </div>

            {/* right: highlight image */}
            <div className="lg:col-span-5">
              <Reveal mount delay={0.15}>
                <div className="relative">
                  <div className="rounded-3xl overflow-hidden shadow-xl ring-1 ring-slate-200 aspect-[4/5]">
                    <img
                      src={data.heroImg}
                      alt={data.h1}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <span className="pointer-events-none absolute -top-3 -left-3 h-16 w-16 border-t-4 border-l-4 border-brand-orange rounded-tl-xl" />
                  <span className="pointer-events-none absolute -bottom-3 -right-3 h-16 w-16 border-b-4 border-r-4 border-brand-orange rounded-br-xl" />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <SectionDivider from={WHITE} to={NAVY} accent={ORANGE} flip />

      {/* CTA BLOCK */}
      <section data-testid="service-cta" className="relative bg-brand-navy py-16 md:py-20 grain-overlay overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(249,115,22,0.10),transparent_45%)]" />
        <div className="relative mx-auto max-w-5xl px-5 sm:px-8 text-center">
          <Reveal mount>
            <h2 className="font-heading font-black text-3xl sm:text-4xl text-white tracking-tight">
              Potřebujete poradit nebo ověřit dostupnost služby?
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-300 max-w-2xl mx-auto">
              Zavolejte nám nebo se rovnou zastavte na provozovně v Ostravě.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3.5">
              <a
                href={PHONE_RAW}
                data-testid="service-cta-call"
                className="inline-flex items-center justify-center gap-2.5 rounded-full bg-brand-orange px-7 py-4 text-base font-bold text-white shadow-xl shadow-brand-orange/30 hover:bg-brand-orangeDark hover:-translate-y-0.5 transition-all duration-300"
              >
                <Phone className="h-5 w-5" strokeWidth={2.6} />
                Zavolat: {PHONE_DISPLAY}
              </a>
              <a
                href={MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="service-cta-navigate"
                className="inline-flex items-center justify-center gap-2.5 rounded-full border-2 border-white/25 px-6 py-4 text-base font-bold text-white hover:border-brand-orange hover:bg-white/5 transition-all duration-300"
              >
                <Navigation className="h-5 w-5 text-brand-orange" strokeWidth={2.4} />
                Navigovat
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <SectionDivider from={NAVY} to={WHITE} accent={ORANGE} />

      {/* CONTACT (reused) */}
      <Kontakt showCtas={false} />

      <Footer onNav={onNav} />
    </>
  );
}
